// EP-009-S5 埋点环形缓冲（主架构 §11 / GDD 02 §G1-G3 / GDD 03 §E1-E2）。
//
// MVP 落点（无后端、无账号，概念 §6 Won't）：
//   1. DEV 模式（import.meta.env.DEV）→ 结构化 console.info('[telemetry]', event, payload)
//   2. 所有环境 → localStorage 环形缓冲（KEY: dogtrip.telemetry，最近 200 条；超出从头弹出）
//
// 7 个事件（逐字取自主架构 §11，不得增删）：
//   trip_started / returned / drop_rolled / revealed / inv_upserted / view_changed / app_returned_to_foreground
//
// 设计纪律：
//   - 落盘失败（quota exceeded / private mode / SecurityError）必须静默 swallow，
//     绝不能 throw 影响主流程（GDD 02 §G1 的可观测性是辅助系统，不阻塞玩法）。
//   - 字段：{ event, ts, payload? } —— ts 由内部 nowMs() 包装 Date.now()。
//   - 本文件位于 src/telemetry/（非 src/domain/），故 Date.now() 允许使用
//     （CI 守门 check-no-tick-accumulator 仅禁用 src/domain/** 内的 Date.now / Math.random）。
//   - SSR / node 测试环境无 localStorage：safeRead / safeWrite 静默降级为空。

const STORAGE_KEY = 'dogtrip.telemetry';
const RING_SIZE = 200;

// ────────────────────────────────────────────────────────────────────────────
// 类型（事件名 = 字面量联合，逐字照主架构 §11，不得增删）
// ────────────────────────────────────────────────────────────────────────────

export type TelemetryEventName =
  | 'trip_started'
  | 'returned'
  | 'drop_rolled'
  | 'revealed'
  | 'inv_upserted'
  | 'view_changed'
  | 'app_returned_to_foreground';

export interface TelemetryEvent {
  event: TelemetryEventName;
  ts: number;
  payload?: Record<string, unknown>;
}

// ────────────────────────────────────────────────────────────────────────────
// 内部 helper（不导出）
// ────────────────────────────────────────────────────────────────────────────

// 内部时间来源（封装 Date.now，便于将来注入 fake timer；本文件允许 Date.now，
// 因为不在 src/domain/** 下 —— CI 守门不会拦）。
function nowMs(): number {
  return Date.now();
}

// 检测 DEV 模式。Vite 在 build/dev/test 阶段均会注入 import.meta.env；
// 在纯 node 测试下可能不存在，故用 optional chaining + try 兜底。
function isDevMode(): boolean {
  try {
    return Boolean((import.meta as { env?: { DEV?: unknown } }).env?.DEV);
  } catch {
    return false;
  }
}

// 安全读 localStorage。SSR / 无 window 环境下降级空数组。
// 损坏数据（JSON.parse 失败 / 形态不符）一律静默丢弃，绝不抛。
function safeRead(): TelemetryEvent[] {
  try {
    if (typeof localStorage === 'undefined') return [];
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((e): e is TelemetryEvent => {
      if (typeof e !== 'object' || e === null) return false;
      const obj = e as Record<string, unknown>;
      return typeof obj.event === 'string' && typeof obj.ts === 'number';
    });
  } catch {
    return [];
  }
}

// 安全写 localStorage。quota exceeded / SecurityError 一律静默 swallow，
// 是埋点的核心纪律：观测系统故障不能上抛到主流程。
function safeWrite(events: TelemetryEvent[]): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
  } catch {
    // 静默 swallow —— 主流程绝不感知埋点系统的存在
  }
}

// 构造环形缓冲条目。exactOptionalPropertyTypes 模式下 payload 不可为 undefined，
// 故分两路构造（payload 不存在时不写该 key，而不是写 key: undefined）。
function makeEntry(
  event: TelemetryEventName,
  payload: Record<string, unknown> | undefined,
): TelemetryEvent {
  return payload !== undefined
    ? { event, ts: nowMs(), payload }
    : { event, ts: nowMs() };
}

// ────────────────────────────────────────────────────────────────────────────
// 导出 API
// ────────────────────────────────────────────────────────────────────────────

// 记录一条事件。同步、立即落盘、永不抛。
//
// 设计选择：MVP 不做批量合并 / 节流（场景稀疏：单次旅行 1 trip_started + 1 returned +
// 1 drop_rolled + 1 revealed + 1 view_changed 量级）。如未来量大再考虑合并。
export function track(
  event: TelemetryEventName,
  payload?: Record<string, unknown>,
): void {
  const entry = makeEntry(event, payload);

  // DEV 模式：结构化 console（playtest 期肉眼可见）
  if (isDevMode()) {
    // eslint-disable-next-line no-console
    console.info('[telemetry]', event, payload);
  }

  // 环形缓冲：尾部 push，超 RING_SIZE 从头部弹出（最旧优先丢）
  const buf = safeRead();
  buf.push(entry);
  if (buf.length > RING_SIZE) {
    buf.splice(0, buf.length - RING_SIZE);
  }
  safeWrite(buf);
}

// 读全部缓冲（按时间顺序）。debug 面板 / 手动导出用。
// 注：未来若加 debug 面板，本函数直接喂即可（这次不实现 UI）。
export function readRecent(): TelemetryEvent[] {
  return safeRead();
}

// 清空环形缓冲。手动重置 / 测试隔离用。
export function clear(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // 静默 swallow
  }
}